/**
 * Composer attachments for Kory chat: file attach, image paste, and
 * large-text paste.
 *
 * Wire format (mirrors the cell-context pattern):
 *  - Text attachments ride INSIDE the message body as
 *    `<attached-document name="…" size="…">content</attached-document>`
 *    blocks, so they reach the model verbatim and survive the base64
 *    content-encoding WAF bypass like the rest of the body.
 *  - Images can't ride in text — they go on the stream request's `images`
 *    field (base64 payloads) and the backend turns them into image content
 *    blocks. An `<attached-images>` JSON manifest in the body carries their
 *    filenames/sizes so the agent can refer to them by name and the history
 *    view can re-render chips after a reload (the binary itself isn't
 *    echoed back).
 * The backend strips both block kinds from the user-visible text in history
 * reconstruction (kory_chat.py `_extract_attachments`).
 */

export interface KoryComposerAttachment {
  id: string;
  kind: "image" | "document" | "spreadsheet" | "pdf";
  name: string;
  mimeType: string;
  sizeBytes: number;
  /** Images + spreadsheets: full data URL — thumbnail (images) / API binary
   * payload. Spreadsheets ride as base64 bytes; the backend parses them. */
  dataUrl?: string;
  /** Documents only: the text content sent to the model. */
  textContent?: string;
  /** Images only: set when the composer shrank an image past the API's pixel
   * limit — its original and sent dimensions, for the composer's notice. */
  shrunk?: { fromWidth: number; fromHeight: number; width: number; height: number };
}

/** Chip-only shape rendered on sent messages (live + history reload). */
export interface KoryMessageAttachment {
  kind: "image" | "document" | "spreadsheet" | "pdf";
  name: string;
  mimeType?: string;
  sizeBytes?: number;
  /** Present on live-sent images only (history doesn't echo the bytes). */
  previewUrl?: string;
  /** Present on live-sent documents only — the viewer shows it without a
   * round trip (history reloads fetch the stored upload instead). */
  textContent?: string;
}

// Mirrors the backend/Slack allowlist (_IMAGE_MIME_TYPES) — the only types
// Anthropic image blocks accept.
export const IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

// Image MIME by file extension — for bytes fetched back from the relay by
// name (the stored upload carries no content-type). Returns null for
// non-image names. The four extensions mirror the upload allowlist above.
const IMAGE_MIME_BY_EXT: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
};

export function imageMimeForName(name: string): string | null {
  const ext = name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
  return IMAGE_MIME_BY_EXT[ext] ?? null;
}

// Files we read as text and inline as <attached-document> blocks. Many
// editors/OSes attach code files with an empty `File.type`, so extensions
// are the fallback signal.
const TEXT_MIME_PREFIXES = ["text/"];
const TEXT_MIME_TYPES = new Set([
  "application/json",
  "application/xml",
  "application/x-yaml",
  "application/yaml",
  "application/sql",
  "application/javascript",
  "application/typescript",
  "application/csv",
]);
const TEXT_EXTENSIONS = new Set([
  "txt", "md", "markdown", "csv", "tsv", "json", "yaml", "yml", "xml",
  "html", "css", "js", "jsx", "ts", "tsx", "py", "sql", "sh", "toml",
  "ini", "cfg", "conf", "env", "log",
]);

// Excel workbooks ride as base64 bytes and are flattened to text on the
// backend (the browser can't read a binary .xlsx as text). Only .xlsx — the
// OOXML format openpyxl reads; legacy .xls is not supported.
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// PDFs also ride as base64 bytes; the backend extracts their text (pdfplumber).
const PDF_MIME = "application/pdf";

// Caps match the backend's request validation (kory_chat.py). Per-file and
// aggregate are DECODED byte sizes; base64 inflates ~4/3 on the wire.
export const MAX_ATTACHMENT_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_ATTACHMENT_TOTAL_BYTES = 12 * 1024 * 1024;
export const MAX_ATTACHMENT_COUNT = 10;

export interface AttachmentLimits {
  fileBytes: number;
  totalBytes: number;
}

export const KORY_CHAT_ATTACHMENT_LIMITS: AttachmentLimits = {
  fileBytes: MAX_ATTACHMENT_FILE_BYTES,
  totalBytes: MAX_ATTACHMENT_TOTAL_BYTES,
};

// Kory Code uploads go to the relay, not kory_chat.py, so they carry their own
// caps. These mirror the relay's per-request decoded cap (sessions.mjs
// ATTACH_MAX_BYTES).
export const KORY_CODE_ATTACHMENT_LIMITS: AttachmentLimits = {
  fileBytes: 20 * 1024 * 1024,
  totalBytes: 40 * 1024 * 1024,
};

// The Claude API rejects an image wider or taller than 8000 px, and a chat
// re-sends its images on every later turn, so one such image fails the whole
// chat. The model reads at most 2576 px on the long edge (it downscales
// anything larger), so the composer shrinks an over-limit image to that size:
// the model sees what it would have seen at full size.
export const MAX_IMAGE_EDGE_PX = 8000;
export const SHRUNK_IMAGE_EDGE_PX = 2576;

// A paste longer than this becomes a "Pasted text" attachment instead of
// flooding the textarea — keeps the composer usable and the user's actual
// question visible when they drop in a whole draft or log dump.
export const LARGE_PASTE_THRESHOLD = 2500;

export function classifyFile(file: File): "image" | "document" | "spreadsheet" | "pdf" | "unsupported" {
  if (IMAGE_MIME_TYPES.has(file.type)) return "image";
  const ext = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  // Excel: trust the OOXML MIME or the .xlsx extension (the OS sometimes
  // attaches it with an empty File.type).
  if (file.type === XLSX_MIME || ext === "xlsx") return "spreadsheet";
  if (file.type === PDF_MIME || ext === "pdf") return "pdf";
  if (TEXT_MIME_PREFIXES.some((p) => file.type.startsWith(p))) return "document";
  if (TEXT_MIME_TYPES.has(file.type)) return "document";
  if (!file.type && TEXT_EXTENSIONS.has(ext)) return "document";
  return "unsupported";
}

/**
 * Validate adding `file` to the pending set. Returns a user-facing error
 * string, or null when the add is allowed.
 */
export function validateAdd(
  existing: KoryComposerAttachment[],
  file: { name: string; size: number },
  kind: "image" | "document" | "spreadsheet" | "pdf" | "unsupported",
  limits: AttachmentLimits = KORY_CHAT_ATTACHMENT_LIMITS,
): string | null {
  if (kind === "unsupported") {
    return `${file.name}: unsupported type — attach images (PNG, JPEG, GIF, WebP), text files, Excel (.xlsx), or PDF (.pdf).`;
  }
  if (existing.length >= MAX_ATTACHMENT_COUNT) {
    return `Attachment limit reached (${MAX_ATTACHMENT_COUNT} per message).`;
  }
  if (file.size > limits.fileBytes) {
    return `${file.name}: too large — max ${formatBytes(limits.fileBytes)} per file.`;
  }
  const total = existing.reduce((sum, a) => sum + a.sizeBytes, 0);
  if (total + file.size > limits.totalBytes) {
    return `${file.name}: attachments exceed ${formatBytes(limits.totalBytes)} total per message.`;
  }
  return null;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/** Shrink an image whose long edge is past MAX_IMAGE_EDGE_PX down to
 * SHRUNK_IMAGE_EDGE_PX. Null when it already fits, or when the browser can't
 * decode or re-encode it — the image is then sent as it is. */
async function shrinkOversizedImage(
  file: File,
): Promise<{ blob: Blob; shrunk: NonNullable<KoryComposerAttachment["shrunk"]> } | null> {
  if (typeof createImageBitmap !== "function") return null;
  let bitmap: ImageBitmap;
  try {
    // The canvas drops EXIF, so bake a photo's rotation into the pixels.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return null;
  }
  try {
    const { width, height } = bitmap;
    const longEdge = Math.max(width, height);
    if (longEdge <= MAX_IMAGE_EDGE_PX) return null;
    const scale = SHRUNK_IMAGE_EDGE_PX / longEdge;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    // Canvas can't encode GIF; PNG keeps it lossless.
    const type = file.type === "image/gif" ? "image/png" : file.type;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type));
    if (!blob) return null;
    return { blob, shrunk: { fromWidth: width, fromHeight: height, width: canvas.width, height: canvas.height } };
  } finally {
    bitmap.close();
  }
}

/** Read a classified file into an attachment. Caller validates first. */
export async function fileToAttachment(
  file: File,
  kind: "image" | "document" | "spreadsheet" | "pdf",
): Promise<KoryComposerAttachment> {
  if (kind === "image" || kind === "spreadsheet" || kind === "pdf") {
    // All ride as base64 bytes. Images render a thumbnail from the data URL;
    // spreadsheets/PDFs are parsed to text server-side (no client-side
    // preview). A spreadsheet/PDF mimeType is pinned to its canonical type —
    // the OS sometimes reports .xlsx as application/zip / x-zip-compressed and
    // .pdf with an empty type, and the backend only accepts the exact MIMEs.
    const shrink = kind === "image" ? await shrinkOversizedImage(file) : null;
    // The encoder may fall back to PNG, so a shrunk image takes the blob's type.
    const payload = shrink?.blob ?? file;
    return {
      id: crypto.randomUUID(),
      kind,
      name: file.name || (kind === "image" ? "Pasted image" : kind === "spreadsheet" ? "Spreadsheet" : "Document"),
      mimeType: kind === "spreadsheet" ? XLSX_MIME : kind === "pdf" ? PDF_MIME : payload.type,
      sizeBytes: payload.size,
      dataUrl: await readAsDataUrl(payload),
      ...(shrink && { shrunk: shrink.shrunk }),
    };
  }
  const text = await readAsText(file);
  return {
    id: crypto.randomUUID(),
    kind,
    name: file.name || "Pasted text",
    mimeType: file.type || "text/plain",
    sizeBytes: new TextEncoder().encode(text).length,
    textContent: text,
  };
}

export function attachmentFromPastedText(text: string): KoryComposerAttachment {
  return {
    id: crypto.randomUUID(),
    kind: "document",
    name: "Pasted text",
    mimeType: "text/plain",
    sizeBytes: new TextEncoder().encode(text).length,
    textContent: text,
  };
}

/**
 * Serialize the pending attachments into the message-body prefix: one
 * <attached-document> block per document plus a single <attached-images>
 * manifest when images are present. Returns "" when there's nothing to add.
 */
export function serializeAttachmentsBlock(atts: KoryComposerAttachment[]): string {
  const parts: string[] = [];
  for (const a of atts) {
    if (a.kind !== "document" || a.textContent === undefined) continue;
    parts.push(
      [
        `<attached-document name=${JSON.stringify(a.name)} size="${a.sizeBytes}">`,
        a.textContent,
        "</attached-document>",
      ].join("\n"),
    );
  }
  const images = atts.filter((a) => a.kind === "image");
  if (images.length) {
    parts.push(
      [
        `<attached-images count="${images.length}">`,
        JSON.stringify(
          images.map((a) => ({
            name: a.name,
            mediaType: a.mimeType,
            sizeBytes: a.sizeBytes,
          })),
        ),
        "</attached-images>",
      ].join("\n"),
    );
  }
  return parts.join("\n\n");
}

/** Image payloads for the stream request's `images` field. */
export function imagesPayload(
  atts: KoryComposerAttachment[],
): { name: string; media_type: string; data: string }[] {
  return atts
    .filter((a) => a.kind === "image" && a.dataUrl)
    .map((a) => ({
      name: a.name,
      media_type: a.mimeType,
      // dataUrl is "data:<mime>;base64,<payload>" — send just the payload.
      data: a.dataUrl!.slice(a.dataUrl!.indexOf(",") + 1),
    }));
}

/** Binary-document payloads for the stream request's `files` field (.xlsx,
 * .pdf). The backend parses these to text and folds them into the user turn. */
export function filesPayload(
  atts: KoryComposerAttachment[],
): { name: string; media_type: string; data: string }[] {
  return atts
    .filter((a) => (a.kind === "spreadsheet" || a.kind === "pdf") && a.dataUrl)
    .map((a) => ({
      name: a.name,
      // Always the canonical MIME by kind — the backend allowlists only these;
      // an OS-reported application/zip etc. would be rejected (see fileToAttachment).
      media_type: a.kind === "pdf" ? PDF_MIME : XLSX_MIME,
      // dataUrl is "data:<mime>;base64,<payload>" — send just the payload.
      data: a.dataUrl!.slice(a.dataUrl!.indexOf(",") + 1),
    }));
}

/** Chip shapes stored on the sent KoryMessage for rendering. */
export function toMessageAttachments(
  atts: KoryComposerAttachment[],
): KoryMessageAttachment[] {
  return atts.map((a) => ({
    kind: a.kind,
    name: a.name,
    mimeType: a.mimeType,
    sizeBytes: a.sizeBytes,
    previewUrl: a.kind === "image" ? a.dataUrl : undefined,
    textContent: a.kind === "document" ? a.textContent : undefined,
  }));
}
